export interface CrmEmailMessage {
  id: string;
  subject: string;
  preview: string;
  from: string;
  to: string[];
  cc: string[];
  bcc: string[];
  bodyAddresses: string[];
  date: string;
  isRead: boolean;
  isSent: boolean;
  isDeleted: boolean;
  isArchived: boolean;
  isSpam: boolean;
  folder: string | null;
}

export interface CrmEmailThread {
  id: string;
  subject: string;
  preview: string;
  latestDate: string;
  latestFrom: string;
  latestTo: string[];
  messageCount: number;
  unreadCount: number;
  messages: CrmEmailMessage[];
}

const EMAIL_PATTERN_GLOBAL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

const normalizeEmail = (value: string): string => String(value || '').trim().toLowerCase();

const normalizeBool = (value: any, fallback = false): boolean => {
  if (value === true) return true;
  if (value === false) return false;
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value && typeof value === 'object') {
    if (value.BOOL === true || value.BOOL === false) return value.BOOL === true;
    if (value.BOOL?.BOOL === true || value.BOOL?.BOOL === false) return value.BOOL.BOOL === true;
  }
  return fallback;
};

const toTimestampMs = (dateValue: string): number => {
  const ms = new Date(dateValue).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

const FORWARDED_MARKER_PATTERN = /(forwarded message|begin forwarded message|original message)/i;
const HEADER_METADATA_LINE_PATTERN = /^(from|to|cc|bcc|date|sent|subject|reply-to)\s*:/i;
const ONLY_EMAIL_LINE_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const URL_ONLY_LINE_PATTERN = /^https?:\/\/\S+$/i;
const DATETIME_EMAIL_METADATA_PATTERN =
  /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)\b.*\b\d{1,2}:\d{2}\b.*@/i;

const stripHtmlToText = (html: string): string => {
  if (!html) return '';
  return String(html)
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr|h1|h2|h3|h4|h5|h6)>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const normalizeTextForPreviewLines = (value: string): string[] =>
  String(value || '')
    .replace(/\r/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .filter(Boolean);

const isNoisePreviewLine = (line: string): boolean => {
  if (!line) return true;
  if (line.startsWith('>')) return true;
  if (/^[-_=]{3,}$/.test(line)) return true;
  if (FORWARDED_MARKER_PATTERN.test(line)) return true;
  if (HEADER_METADATA_LINE_PATTERN.test(line)) return true;
  if (/^on .+ wrote:$/i.test(line)) return true;
  if (ONLY_EMAIL_LINE_PATTERN.test(line)) return true;
  if (URL_ONLY_LINE_PATTERN.test(line)) return true;
  if (DATETIME_EMAIL_METADATA_PATTERN.test(line)) return true;
  return false;
};

const trimPreviewToWordBoundary = (value: string, maxLen: number): string => {
  const normalized = String(value || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  if (normalized.length <= maxLen) return normalized;

  const bounded = normalized.slice(0, maxLen + 1);
  const lastSpace = bounded.lastIndexOf(' ');
  const truncated = lastSpace > Math.floor(maxLen * 0.6) ? bounded.slice(0, lastSpace) : bounded.slice(0, maxLen);
  return `${truncated.trim()}...`;
};

const buildPreviewFromText = (value: string, maxLen: number): string => {
  const lines = normalizeTextForPreviewLines(value);
  if (lines.length === 0) return '';

  const forwardedIndex = lines.findIndex((line) => FORWARDED_MARKER_PATTERN.test(line));
  const candidateLines = forwardedIndex >= 0 ? lines.slice(forwardedIndex + 1) : lines;
  const usefulLines = candidateLines.filter((line) => !isNoisePreviewLine(line));
  const fallbackLines = lines.filter((line) => !isNoisePreviewLine(line));
  const selected = usefulLines.length > 0 ? usefulLines : fallbackLines.length > 0 ? fallbackLines : lines;

  let preview = '';
  for (const line of selected) {
    const next = preview ? `${preview} ${line}` : line;
    preview = next;
    if (preview.length >= maxLen) {
      break;
    }
  }

  return trimPreviewToWordBoundary(preview, maxLen);
};

const getPreviewSnippet = (raw: any, maxLen = 1200): string => {
  const text = String(raw?.text || '').trim();
  const textPreview = buildPreviewFromText(text, maxLen);
  if (textPreview) return textPreview;

  const htmlText = stripHtmlToText(String(raw?.html || '').trim());
  const htmlPreview = buildPreviewFromText(htmlText, maxLen);
  if (htmlPreview) return htmlPreview;

  return buildPreviewFromText(String(raw?.preview || ''), maxLen);
};

const normalizeSubjectForThread = (subject: string): string =>
  String(subject || '')
    .replace(/^(\s*(re|fw|fwd)\s*:\s*)+/i, '')
    .trim()
    .toLowerCase();

const getRawEmailsArray = (response: any): any[] => {
  if (Array.isArray(response?.data?.emails)) return response.data.emails;
  if (Array.isArray(response?.emails)) return response.emails;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
};

const getRawEmailDedupKey = (raw: any, index: number): string => {
  const unifiedPk = String(raw?.PK || raw?.pk || raw?.unifiedPk || '').trim();
  const unifiedSk = String(raw?.SK || raw?.sk || raw?.unifiedSk || '').trim();
  const messageId = String(raw?.id || raw?.messageId || '').trim();
  const composite = [unifiedPk, unifiedSk, messageId, raw?.subject, raw?.date, raw?.from]
    .filter(Boolean)
    .join('|')
    .trim();
  return composite || `row-${index}`;
};

export const mergeEmailResponses = (...responses: any[]): { data: { emails: any[] } } => {
  const merged: any[] = [];
  const seen = new Set<string>();

  responses.forEach((response) => {
    getRawEmailsArray(response).forEach((raw, index) => {
      const dedupKey = getRawEmailDedupKey(raw, index);
      if (!dedupKey || seen.has(dedupKey)) return;
      seen.add(dedupKey);
      merged.push(raw);
    });
  });

  return { data: { emails: merged } };
};

export const extractEmailAddresses = (value: unknown): string[] => {
  const unique = new Set<string>();

  const parse = (input: unknown) => {
    if (Array.isArray(input)) {
      input.forEach((entry) => parse(entry));
      return;
    }
    const raw = String(input || '');
    const matches = raw.match(EMAIL_PATTERN_GLOBAL) || [];
    matches.forEach((match) => {
      const normalized = normalizeEmail(match);
      if (normalized) unique.add(normalized);
    });
  };

  parse(value);
  return Array.from(unique);
};

export const mapEmailsFromUniboxResponse = (response: any): CrmEmailMessage[] => {
  const rawEmails = getRawEmailsArray(response);

  return rawEmails
    .map((raw: any) => {
      const unifiedPk = raw?.PK || raw?.pk || raw?.unifiedPk || '';
      const unifiedSk = raw?.SK || raw?.sk || raw?.unifiedSk || '';
      const fallbackId = [unifiedPk, unifiedSk, raw?.messageId, raw?.subject, raw?.date, raw?.from]
        .filter(Boolean)
        .join('|');
      const id = String(raw?.id || raw?.messageId || unifiedSk || fallbackId).trim();
      if (!id) return null;

      const fromList = extractEmailAddresses(raw?.from || raw?.sender || '');
      const toList = extractEmailAddresses(raw?.to);
      const ccList = extractEmailAddresses(raw?.cc);
      const bccList = extractEmailAddresses(raw?.bcc);
      const bodyAddresses = extractEmailAddresses([
        raw?.text || '',
        stripHtmlToText(String(raw?.html || '')),
        raw?.preview || '',
        raw?.subject || '',
      ]);

      return {
        id,
        subject: String(raw?.subject || '(No Subject)'),
        preview: getPreviewSnippet(raw),
        from: fromList[0] || '',
        to: toList,
        cc: ccList,
        bcc: bccList,
        bodyAddresses,
        date: String(raw?.date || raw?.createdAt || new Date().toISOString()),
        isRead: normalizeBool(raw?.isRead, false),
        isSent: normalizeBool(raw?.isSent, false),
        isDeleted: normalizeBool(raw?.isDeleted, false),
        isArchived: normalizeBool(raw?.isArchived, false),
        isSpam: normalizeBool(raw?.isSpam, false),
        folder: raw?.folder != null ? String(raw.folder) : null,
      } as CrmEmailMessage;
    })
    .filter((email): email is CrmEmailMessage => Boolean(email))
    .sort((a, b) => toTimestampMs(b.date) - toTimestampMs(a.date));
};

export const buildEmailThreadsForEntity = (
  response: any,
  entityEmail: string
): CrmEmailThread[] => {
  const target = normalizeEmail(entityEmail);
  if (!target) return [];

  const matchingMessages = mapEmailsFromUniboxResponse(response)
    .filter((email) => !email.isDeleted && !email.isArchived && !email.isSpam)
    .filter((email) => {
      const participants = new Set<string>([
        ...extractEmailAddresses(email.from),
        ...email.to,
        ...email.cc,
        ...email.bcc,
        ...email.bodyAddresses,
      ]);
      return participants.has(target);
    })
    .sort((a, b) => toTimestampMs(b.date) - toTimestampMs(a.date));

  const grouped = new Map<string, CrmEmailMessage[]>();
  matchingMessages.forEach((message) => {
    const normalizedSubject = normalizeSubjectForThread(message.subject) || '(no-subject)';
    // Group by normalized subject so forwarded versions still merge with
    // their original conversation for the same CRM entity.
    const key = normalizedSubject;
    const existing = grouped.get(key) || [];
    existing.push(message);
    grouped.set(key, existing);
  });

  return Array.from(grouped.entries())
    .map(([, messages], index) => {
      const ordered = [...messages].sort((a, b) => toTimestampMs(b.date) - toTimestampMs(a.date));
      const latest = ordered[0];
      return {
        id: `${latest.id}-${index}`,
        subject: latest.subject || '(No Subject)',
        preview: latest.preview || '',
        latestDate: latest.date,
        latestFrom: latest.from,
        latestTo: latest.to,
        messageCount: ordered.length,
        unreadCount: ordered.filter((message) => !message.isRead).length,
        messages: ordered,
      } as CrmEmailThread;
    })
    .sort((a, b) => toTimestampMs(b.latestDate) - toTimestampMs(a.latestDate));
};
